/**
 * Utility helper functions
 */

import { marked } from 'marked';

// Configure marked for chat messages
marked.setOptions({
  breaks: true,   // Convert single \n to <br>
  gfm: true,      // GitHub Flavored Markdown (tables, strikethrough, etc.)
});

// Token estimation function (rough estimate: 1 token ≈ 4 characters)
export function estimateTokens(text) {
  return Math.ceil(text.length / 4);
}

// Markdown to HTML converter for chat messages
function renderMarkdown(text) {
  const normalized = text
    //.replace(/[ \t]+$/gm, '')   // strip trailing spaces per line (prevents accidental <br>)
    //.replace(/\n{3,}/g, '\n\n') // collapse 3+ blank lines to one paragraph break
    .trim();                     // remove leading/trailing newlines
  return marked.parse(normalized).trimEnd();
}

// UI helper functions - moved here to avoid circular dependencies
export function appendMessage(content, isUser = false) {
  const messagesDiv = document.getElementById("chat-messages");
  const messageDiv = document.createElement("div");
  messageDiv.className = `message ${isUser ? "user-message" : "assistant-message"}`;
  if (isUser) {
    messageDiv.textContent = content;
  } else {
    messageDiv.innerHTML = renderMarkdown(content);
  }
  messagesDiv.appendChild(messageDiv);
  messagesDiv.scrollTop = messagesDiv.scrollHeight;
}

export function showThinkingIndicator() {
  const messagesDiv = document.getElementById("chat-messages");
  const thinkingDiv = document.createElement("div");
  thinkingDiv.id = "thinking-indicator";
  thinkingDiv.className = "message assistant-message thinking";
  thinkingDiv.innerHTML = '<span class="thinking-dots">Denke nach<span class="dot">.</span><span class="dot">.</span><span class="dot">.</span></span>';
  messagesDiv.appendChild(thinkingDiv);
  messagesDiv.scrollTop = messagesDiv.scrollHeight;
}

export function hideThinkingIndicator() {
  const thinkingDiv = document.getElementById("thinking-indicator");
  if (thinkingDiv) {
    thinkingDiv.remove();
  }
}

export function validateAndAdjustData(newData, expectedRows, expectedCols) {
  if (!Array.isArray(newData)) {
    throw new Error("Daten müssen ein Array sein");
  }
  
  let adjustedData = newData;
  
  if (newData.length > expectedRows) {
    adjustedData = newData.slice(-expectedRows);
    console.log(`AI returned ${newData.length} rows, taking last ${expectedRows} rows`);
  } else if (newData.length < expectedRows) {
    throw new Error(`Zu wenige Zeilen: Erwartet ${expectedRows}, erhalten ${newData.length}`);
  }
  
  for (let i = 0; i < adjustedData.length; i++) {
    if (!Array.isArray(adjustedData[i])) {
      throw new Error(`Zeile ${i} muss ein Array sein`);
    }
    if (adjustedData[i].length > expectedCols) {
      adjustedData[i] = adjustedData[i].slice(-expectedCols);
    } else if (adjustedData[i].length < expectedCols) {
      throw new Error(`Zeile ${i}: Zu wenige Spalten: Erwartet ${expectedCols}, erhalten ${adjustedData[i].length}`);
    }
  }
  
  return adjustedData;
}

export async function highlightChangedCells(sheet, newData, originalValues, startRow, startCol) {
  if (!sheet) return;
  for (let row = 0; row < newData.length; row++) {
    for (let col = 0; col < newData[row].length; col++) {
      const newValue = newData[row][col];
      const originalValue = originalValues[row] ? originalValues[row][col] : null;
      
      if (newValue !== originalValue) {
        const cell = sheet.getRangeByIndexes(startRow + row, startCol + col, 1, 1);
        cell.format.fill.color = "yellow";
      }
    }
  }
  await sheet.context.sync();
}

export function handleApiError(error, currentModel, appendMessageFn) {
  console.error("Error:", error);
  console.error("Error message:", error.message);
  
  if (error.message && (error.message.includes('Quota') || error.message.includes('überschritten') || error.message.includes('Limit') || error.message.includes('Kontingent') || error.message.includes('erreicht'))) {
    appendMessageFn(`❌ ${error.message}`);
  } else if (error.message && (error.message.includes('Token') || error.message.includes('Anfrage') || error.message.includes('Datenmenge') || error.message.includes('Zeilen'))) {
    appendMessageFn(`❌ ${error.message}\n\nTipp: Wähle weniger Daten aus oder vereinfache deine Anfrage.`);
  } else if (error.message && error.message.includes('Azure OpenAI API error:')) {
    // Specific Azure error with extracted message — show it directly
    appendMessageFn(`❌ ${error.message}`);
  } else if (error.message && error.message === 'Azure OpenAI API error') {
    // Generic Azure error without details — deployment likely missing
    appendMessageFn(`❌ Fehler beim Aufrufen des Modells. Prüfe, ob das Deployment "${currentModel}" in Azure OpenAI existiert.\n\nDetails: ${error.message}`);
  } else if (error.message) {
    appendMessageFn(`❌ Fehler: ${error.message}`);
  } else {
    appendMessageFn("Entschuldigung, bei der Verarbeitung deiner Anfrage ist ein Fehler aufgetreten. Bitte versuche es erneut.");
  }
}